import { useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { useFluidHover } from '@hooks/useFluidHover'
import { spring } from '@utils/motion/springs'

const instant = { duration: 0 }

const trackClasses = cn(
  'tw:relative tw:inline-flex tw:w-fit tw:shrink-0 tw:select-none tw:items-stretch tw:rounded-xl tw:bg-gray-100 tw:p-1',
  'tw:shadow-[inset_0_1px_2px_rgba(16,24,40,0.08)]'
)

const thumbClasses = cn(
  'tw:pointer-events-none tw:absolute tw:rounded-lg tw:bg-white',
  'tw:shadow-[0_1px_2px_rgba(16,24,40,0.06),0_2px_6px_rgba(16,24,40,0.1)] tw:ring-1 tw:ring-black/5'
)

const itemClasses = cn(
  'tw:group tw:relative tw:z-10 tw:flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:rounded-lg tw:px-3.5 tw:py-1.5 tw:text-sm tw:leading-[22px]',
  'tw:text-gray-600 tw:data-[state=active]:text-gray-900',
  'tw:transition-[color,transform] tw:duration-150 tw:active:scale-[0.97] tw:motion-reduce:transform-none',
  'tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-300'
)

export type SegmentedOption<T extends string> = {
  value: T
  label: string
  leadingIcon?: IconTypes
  count?: number
}

export type SegmentedControlProps<T extends string> = {
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  className?: string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  const selectedIndex = options.findIndex((option) => option.value === value)

  const trackRef = useRef<HTMLDivElement | null>(null)
  const [mouseInside, setMouseInside] = useState(false)
  const reduceMotion = useReducedMotion()
  const {
    activeIndex: hoveredIndex,
    setActiveIndex: setHoveredIndex,
    itemRects,
    handlers,
    registerItem,
  } = useFluidHover(trackRef)
  const selectedRect = selectedIndex >= 0 ? itemRects[selectedIndex] : undefined
  const hoverRect = hoveredIndex !== null ? itemRects[hoveredIndex] : undefined
  const isHoveringSelected = hoveredIndex === selectedIndex

  return (
    <div
      ref={trackRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(trackClasses, className)}
      onMouseMove={(e) => {
        setMouseInside(true)
        handlers.onMouseMove(e)
      }}
      onMouseLeave={() => {
        setMouseInside(false)
        handlers.onMouseLeave()
      }}
      onFocus={(e) => {
        const index = e.target.closest('[data-fluid-hover-index]')?.getAttribute('data-fluid-hover-index')
        if (index != null) setHoveredIndex(Number(index))
      }}
      onBlur={(e) => {
        if (trackRef.current?.contains(e.relatedTarget as Node)) return
        if (!mouseInside) setHoveredIndex(null)
      }}
    >
      {selectedRect && (
        <motion.div
          aria-hidden
          className={thumbClasses}
          initial={false}
          animate={selectedRect}
          transition={reduceMotion ? instant : spring.moderate}
        />
      )}
      <AnimatePresence custom={mouseInside}>
        {hoverRect && !isHoveringSelected && selectedRect && (
          <motion.div
            aria-hidden
            className="tw:pointer-events-none tw:absolute tw:rounded-lg tw:bg-white"
            initial={{ ...selectedRect, opacity: 0 }}
            animate={{ ...hoverRect, opacity: 0.4 }}
            variants={{
              leave: (inside: boolean) =>
                inside
                  ? { opacity: 0, transition: { duration: 0.06 } }
                  : {
                      ...selectedRect,
                      opacity: 0,
                      transition: reduceMotion ? instant : { ...spring.moderate, opacity: { duration: 0.06 } },
                    },
            }}
            exit="leave"
            transition={reduceMotion ? instant : { ...spring.fast, opacity: { duration: 0.08 } }}
          />
        )}
      </AnimatePresence>
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            ref={(element) => registerItem(index, element)}
            type="button"
            aria-pressed={selected}
            data-state={selected ? 'active' : 'inactive'}
            data-fluid-hover-index={index}
            onClick={() => onChange(option.value)}
            className={cn(itemClasses, hoveredIndex === index && 'tw:data-[state=inactive]:text-gray-800')}
          >
            {option.leadingIcon && <Icon type={option.leadingIcon} className="tw:h-4 tw:w-4" aria-hidden />}
            {/* An invisible semibold copy holds the width, so the label gets heavier without moving its neighbours. */}
            <span className="tw:inline-grid">
              <span aria-hidden className="tw:invisible tw:col-start-1 tw:row-start-1 tw:font-semibold">
                {option.label}
              </span>
              <span className="tw:col-start-1 tw:row-start-1 tw:font-medium tw:transition-[font-weight] tw:duration-80 tw:group-data-[state=active]:font-semibold">
                {option.label}
              </span>
            </span>
            {option.count !== undefined && (
              <span
                className={cn(
                  'tw:inline-flex tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:px-1.5 tw:text-xs tw:font-semibold tw:leading-5 tw:tabular-nums tw:transition-colors tw:duration-80',
                  selected ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-gray-200/80 tw:text-gray-600'
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
